
import React from 'react';
import { Button } from '@/components/ui/button';
import { Separator } from '@/components/ui/separator';
import { ShieldCheck, Truck } from 'lucide-react';
import CartItem from '@/components/cart/CartItem';
import { CartItem as CartItemType } from '@/store/useCartStore';
import { formatCurrency } from '@/lib/utils';

interface OrderSummaryProps {
  items: CartItemType[];
  subtotal: number;
  shippingCost: number;
  currency: string;
  onSubmit: () => void;
  isSubmitting: boolean;
}

const OrderSummary: React.FC<OrderSummaryProps> = ({
  items,
  subtotal,
  shippingCost,
  currency,
  onSubmit,
  isSubmitting,
}) => {
  const grandTotal = subtotal + shippingCost;

  const handleSubmit = () => {
    window.scrollTo(0, 0);
    onSubmit();
  };

  return (
    <div className="bg-white rounded-lg shadow-sm p-6 sticky top-6">
      <h2 className="text-xl font-semibold mb-4">Order Summary</h2>
      
      <div className="max-h-80 overflow-y-auto mb-4 pr-2">
        {items.map(item => (
          <CartItem key={item.id} item={item} />
        ))}
      </div>
      
      <Separator className="my-4" />
      
      <div className="space-y-3">
        <div className="flex justify-between">
          <span className="text-gray-600">Subtotal</span>
          <span>{formatCurrency(subtotal, currency)}</span>
        </div>
        <div className="flex justify-between">
          <span className="text-gray-600">Shipping</span>
          {shippingCost === 0 ? (
            <span className="text-green-600">Free</span>
          ) : (
            <span>{formatCurrency(shippingCost, currency)}</span>
          )}
        </div>
        <Separator className="my-2" />
        <div className="flex justify-between font-semibold text-lg">
          <span>Total</span>
          <span>{formatCurrency(grandTotal, currency)}</span>
        </div>
      </div>
      
      <Button 
        type="button"
        className="w-full mt-6 bg-ahavex-blue hover:bg-ahavex-darkblue text-white font-medium"
        onClick={handleSubmit}
        disabled={isSubmitting}
      >
        {isSubmitting ? 'Processing...' : 'Place Order'}
      </Button>
      
      <div className="mt-6 space-y-3">
        <div className="flex items-center text-sm text-gray-600">
          <Truck className="w-4 h-4 mr-2" />
          <span>Free shipping on orders over RWF 100k</span>
        </div>
        <div className="flex items-center text-sm text-gray-600">
          <ShieldCheck className="w-4 h-4 mr-2" />
          <span>Secure payment processing</span>
        </div>
      </div>
    </div>
  );
};

export default OrderSummary;
